import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import ComPDFKitViewer from "@compdfkit_pdf_sdk/webviewer";
import { Container, Header } from "@cloudscape-design/components";
import useIsLoading from "../../../hooks/useIsLoading";
import {
  convertStringToCustomDataObj,
  convertCustomDataObjToString,
  getValueFromProviderData,
  getFormFieldTypeFromAnnote,
  getFormFieldType,
} from "../../../utils/compdf-annote-helper.js";
import { useGetDigitalFormQuery } from "../../../redux/api/digitalform/digitalforms";
import { useGetAllRecordsByProviderIdQuery } from "../../../redux/api/section/providerSection";
import { isArray } from "lodash";
import { COMPDFSignatureRequestTagsList } from "./compdf-signature-request-tags-list";
import { captureException } from "@sentry/react";
import { getFileUrl } from "../../../config";
import { useGetProviderQuery } from "../../../redux/api/provider/provider";

import { Col, Row } from "antd";
import { skipToken } from "@reduxjs/toolkit/query";
import StickyFooter from "../../../components/StickyFooter";
import IncredableButton from "../../../components/button";

export function ComPdfSignatureRequestStepTwo({
  providerId,
  onClickBack,
  handleWizardSubmit,
}) {
  const { digitalformId } = useParams();

  const viewer = useRef(null);

  const [instance, setInstance] = useState(undefined);
  const [documentLoaded, setDocumentLoaded] = useState(false);
  const [annoteImported, setAnnoteImported] = useState(false);

  const { data: digitalform } = useGetDigitalFormQuery(
    digitalformId ?? skipToken
  );

  const { data: provider, isLoading: isProviderDataloading } =
    useGetProviderQuery(
      !!providerId
        ? {
            providerId: providerId,
          }
        : skipToken
    );

  const {
    data: providerDynamicProfiles,
    isLoading: isProviderDynamicProfiles,
  } = useGetAllRecordsByProviderIdQuery(
    !!providerId
      ? {
          providerId: providerId,
        }
      : skipToken
  );

  const data = useMemo(() => {
    if (!provider || !providerDynamicProfiles) return {};
    return {
      ...provider,
      ...providerDynamicProfiles,
    };
  }, [provider, providerDynamicProfiles]);
  useIsLoading(
    isProviderDynamicProfiles || isProviderDataloading || !documentLoaded
  );

  useEffect(() => {
    if (viewer.current != null && !!digitalform?.flattenedTemplate?.key)
      ComPDFKitViewer.init(
        {
          path: "/",
          license: "Njg0YmEwYjM5OTZkNw==",
        },
        viewer.current
      )
        .then((instance) => {
          setInstance(instance);
          const { UI } = instance;
          UI.textPopup.update([]);
          UI.disableElements(["header"]);
          // Hide the top tab bar of e-signature
          UI.setEnableSignatureTemplates(false);

          // Set e-signature input methods: Image, Keyboard, Trackpad
          UI.setSignatureDialogTabs(["trackpad", "image"]);
          instance.UI.closeElement("leftPanel");

          if ("localhost" === window.location.hostname) {
            instance.UI.loadDocument("./example/developer_guide_web.pdf");
          } else {
            instance.UI.loadDocument(
              encodeURI(getFileUrl(digitalform?.flattenedTemplate?.key)),
              {
                withCredentials: true,
              }
            );
          }
          let docViewer = instance.docViewer;
          docViewer.addEvent("documentloaded", async () => {
            // docViewer.setToolMode("form");
            instance.UI.closeElement("rightPanel");
            const pageWidth = instance.docViewer.getPageWidth(1);
            const viewerContainer = document.getElementById("compdf");
            const viewerWidth = viewerContainer.clientWidth;
            docViewer.webViewerScaleChanged(viewerWidth / pageWidth);
            // Hide the top tab bar of e-signature
            instance.UI.setEnableSignatureTemplates(false);

            // Set e-signature input methods: Image, Keyboard, Trackpad
            instance.UI.setSignatureDialogTabs([
              "trackpad",
              "keyboard",
              "image",
            ]);
            setDocumentLoaded(true);
          });
        })
        .catch((error) => {
          captureException(error);
          console.log(error);
        });
  }, [digitalform]);

  // adds FreeTextAnnotation
  const addFreeTextAnnotation = (point, field) => {
    if (!instance) return;

    const { docViewer } = instance;

    const page = docViewer.getSelectedPage(point, point);
    const width = field.type === "bool" ? 20 : 180;
    const height = field.type === "bool" ? 40 : 50;

    if (!!point.x && page.first == null) {
      console.warn("invalid page location");
      return; //don't add field to an invalid page location
    }

    const page_idx =
      page.first !== null ? page.first : docViewer.getCurrentPage();

    //  const page_info = doc.getPageInfo(page_idx); getPageWidth(pageNumber)
    const page_point = docViewer.windowToPage(point, page_idx);

    const pageIndex = page_idx;

    const X = page_point.x + 8;
    const Y = page_point.y - 12;
    let customData = { ...field };

    if (
      field.isList &&
      !!docViewer.getAnnotationManager().getAnnotationsList()
    ) {
      try {
        const flattened = Object.values(
          docViewer.getAnnotationManager().getAnnotationsList()
        ).flat();
        const index = flattened?.filter((annot) => {
          const annoteCustomData = convertStringToCustomDataObj(
            annot.fieldName
          );
          return annoteCustomData?.key + "" === field.key.toString();
        }).length;
        customData = { ...customData, index: index };
      } catch (error) {
        console.log(error);
      }
    }

    docViewer.addAnnotations({
      type: getFormFieldType(customData?.type),
      rect: {
        left: X,
        top: Y,
        right: X + width,
        bottom: Y + height / 2,
      },
      fieldName: convertCustomDataObjToString(customData),
      isHidden: 0,
      contents: getValueFromProviderData(
        data,
        convertCustomDataObjToString(customData),
        false
      ),
      backgroundColor: "#93B9FD",
      fillColor: "#93B9FD",
      color: "#000000",
      fontName: "Helvetica",
      fontSize: 14,
      checkStyle: 1,
      textAlignment: "left",
      isMultiLine: false,
      pageIndex: pageIndex - 1,
    });
  }; //end add field

  const dragStart = (e) => {
    e.currentTarget.style.opacity = "0.5";
    const copy = e.currentTarget.cloneNode(true);
    copy.id = "form-build-drag-image-copy";
    copy.style.width = "250px";
    document.body.appendChild(copy);
    e.dataTransfer.setDragImage(copy, 10, 25);
    e.dataTransfer.setData("text", "");
  };
  const dragEnd = (e, field) => {
    const { docViewer } = instance;
    const scrollElement = docViewer.getScrollViewElement();
    const scrollLeft = scrollElement.scrollLeft || 0;
    const scrollTop = scrollElement.scrollTop || 0;
    addFreeTextAnnotation(
      { x: e.pageX + scrollLeft, y: e.pageY + scrollTop },
      field
    );

    e.currentTarget.style.opacity = "1";
    document.body.removeChild(
      document.getElementById("form-build-drag-image-copy")
    );
    e.preventDefault();
  };

  useEffect(() => {
    if (
      !documentLoaded ||
      annoteImported ||
      !provider ||
      !instance ||
      !digitalform ||
      !digitalform?.annotationsJson
    )
      return;
    const docViewer = instance.docViewer;
    const annotes = JSON.parse(digitalform?.annotationsJson);
    if (!!annotes && isArray(annotes)) {
      annotes?.forEach((annote) => {
        delete annote?.date;
        console.log({ annote });
        docViewer.addAnnotations({
          ...annote,
          type: getFormFieldTypeFromAnnote(annote),
          checkStyle: 1,
          contents: getValueFromProviderData(data, annote?.fieldName, false),
        });
      });
    }
    setAnnoteImported(true);
  }, [documentLoaded, annoteImported, provider, digitalform, instance]);

  const submitSignatureRequest = async () => {
    if (!instance) return;
    const { docViewer } = instance;
    const annotes = docViewer.getAnnotationManager().getAnnotationsList();

    const formFieldAnnotes = annotes.map((annote) => {
      return {
        ...annote?.annotation,
      };
    });
    const annotationJSON = JSON.stringify(formFieldAnnotes);
    console.log({ formFieldAnnotes, annotationJSON });
    handleWizardSubmit(annotationJSON);
  };

  return (
    <>
      <div
        style={{
          position: "relative",
        }}
      >
        <div style={{ marginBottom: "12px" }}>
          <Header variant="h1">{digitalform?.name} Request signature</Header>
        </div>
        <div
          style={{
            height: "calc(100vh - 180px)",
            overflowY: "scroll",
            scrollbarWidth: "none",
            paddingBottom: "70px",
          }}
        >
          <Container fitHeight>
            <div
              ref={viewer}
              style={{ height: `calc(100vh - 176px)` }}
              id="compdf"
            />
          </Container>

          <StickyFooter
            hasPermission={true}
            onSubmit={() => {
              submitSignatureRequest();
            }}
            extras={
              <>
                <IncredableButton
                  secondary
                  onClick={async () => {
                    if (!instance) return;
                    await downloadFile(instance);
                  }}
                >
                  Download
                </IncredableButton>
              </>
            }
            cancelText="Back"
            onCancel={() => {
              onClickBack && onClickBack();
            }}
            saveText="Submit"
          />
        </div>
      </div>
    </>
  );
}

const downloadFile = async (instance) => {
  try {
    const arrayBuffer = await instance.docViewer.download();

    const blob = new Blob([arrayBuffer], { type: "application/pdf" }); // or other MIME type
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "document.pdf"; // You can make this dynamic
    document.body.appendChild(a);
    a.click();

    // Cleanup
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Download failed", error);
  }
};
